/**
 * Store central do app. Mantem apenas o estado minimo necessario para o fluxo:
 * privacidade aceita, premissas, resultado de diagnostico (em memoria).
 *
 * Privacidade: nada e persistido em localStorage/sessionStorage. Refresh limpa estado.
 */
import { writable, type Writable, get } from "svelte/store";
import type { LocalInputFile } from "../core/files/file.types";
import type { DiagnosticAssumptions } from "../core/domain/canonical.types";
import type { DiagnosticResult } from "../core/diagnostic.types";
import type { WorkerClientState } from "../workers/workerClient";
import {
  DEFAULT_TOLERANCE_MINUTES,
  DEFAULT_NIGHT_HOUR_START,
  DEFAULT_NIGHT_HOUR_END,
  DEFAULT_DAILY_HOURS,
  DEFAULT_MONTHLY_HOURS,
  DEFAULT_EXCESSIVE_JOURNEY_MINUTES,
  DEFAULT_INTRADAY_BREAK_MINUTES,
  DEFAULT_INTERJOURNEY_MINUTES,
  DEFAULT_CORRECTION_MINUTES_PER_EVENT,
} from "./constants";

export type AnalysisStatus =
  | "idle"
  | "loading"
  | "running"
  | "ready"
  | "error";

export type AppStep =
  | "auth"
  | "privacy"
  | "import"
  | "assumptions"
  | "analyzing"
  | "dashboard"
  | "error";

/** Premissas default usadas enquanto o usuario nao configura o formulario. */
export const DEFAULT_ASSUMPTIONS: DiagnosticAssumptions = {
  dailyHours: DEFAULT_DAILY_HOURS,
  monthlyHours: DEFAULT_MONTHLY_HOURS,
  toleranceMinutes: DEFAULT_TOLERANCE_MINUTES,
  excessiveJourneyMinutes: DEFAULT_EXCESSIVE_JOURNEY_MINUTES,
  shortIntradayBreakMinutes: DEFAULT_INTRADAY_BREAK_MINUTES,
  shortInterjourneyMinutes: DEFAULT_INTERJOURNEY_MINUTES,
  nightStartHour: DEFAULT_NIGHT_HOUR_START,
  nightEndHour: DEFAULT_NIGHT_HOUR_END,
  correctionMinutesPerEvent: DEFAULT_CORRECTION_MINUTES_PER_EVENT,
};

/** Estado de drill-down ativo (clique em tile de métrica). Não persiste. */
export interface DrillDownState {
  metricId: string;
  /**
   * Quando definido, mostra breakdown diário desse colaborador.
   * Quando undefined, mostra o ranking de colaboradores.
   */
  focusedEmployeeId?: string;
}

export interface AppState {
  /** Aceite de aviso local-first feito na sessao corrente. */
  privacyAccepted: boolean;
  /** Status do pipeline de analise. */
  status: AnalysisStatus;
  /** Mensagem de erro amigavel quando status === "error". */
  errorMessage: string | null;
  /** Arquivos carregados pelo usuario nesta sessao (apenas metadados seguros exibidos na UI). */
  loadedFiles: LocalInputFile[];
  /** Premissas configuradas pelo usuario no formulario (T24). */
  assumptions: DiagnosticAssumptions;
  /** Etapa atual do fluxo (state machine). */
  currentStep: AppStep;
  /** Estado do worker durante a analise. Null fora do passo 'analyzing'. */
  analysisProgress: WorkerClientState | null;
  /** Resultado do diagnostico. Disponivel apos analise bem-sucedida. */
  result: DiagnosticResult | null;
  /** Drill-down ativo (clique em tile de métrica). Null quando fechado. Nao persiste. */
  drillDown: DrillDownState | null;
}

export interface AppStore {
  state: Writable<AppState>;
  acceptPrivacy(): void;
  reset(): void;
  /**
   * Limpa a sessao de diagnostico, preservando privacyAccepted=true (decisao UX T30).
   * Zera loadedFiles, assumptions, status, errorMessage,
   * currentStep (volta a 'import'), analysisProgress e result.
   */
  clearSession(): void;
  snapshot(): AppState;
  addLoadedFiles(files: LocalInputFile[]): void;
  /** Atualiza campos parciais das premissas sem sobrescrever os demais. */
  updateAssumptions(partial: Partial<DiagnosticAssumptions>): void;
  /** Avanca para uma nova etapa do fluxo. */
  setCurrentStep(step: AppStep): void;
  /** Atualiza o progresso do worker em andamento. */
  setAnalysisProgress(s: WorkerClientState | null): void;
  /** Armazena o resultado do diagnostico apos analise bem-sucedida. */
  setResult(r: DiagnosticResult | null): void;
  /** Abre o drill-down para a métrica especificada. */
  openDrillDown(metricId: string): void;
  /** Fecha o drill-down ativo. */
  closeDrillDown(): void;
  /** Navega para o breakdown diário de um colaborador específico no drill-down ativo. */
  focusEmployeeInDrillDown(employeeId: string): void;
  /** Volta para o ranking (remove foco do colaborador), mantendo o drill-down aberto. */
  unfocusEmployee(): void;
}

/**
 * Apenas estes steps são "lugares" estáveis na navegação. Privacy (modal),
 * analyzing (progresso) e error (terminal) ficam fora — entrar neles via
 * back/forward criaria estado inválido (ex.: voltar pra "analyzing" sem worker).
 */
const NAVIGABLE_STEPS: ReadonlySet<AppStep> = new Set(['import', 'assumptions', 'dashboard']);

interface NavHistoryState {
  step: AppStep;
  drillDownId: string | null;
  focusedEmployeeId: string | null;
}

export function createAppStore(): AppStore {
  const initial: AppState = {
    privacyAccepted: false,
    status: "idle",
    errorMessage: null,
    loadedFiles: [],
    assumptions: { ...DEFAULT_ASSUMPTIONS },
    currentStep: "auth",
    analysisProgress: null,
    result: null,
    drillDown: null,
  };
  const state = writable<AppState>({ ...initial });

  const isBrowser = typeof window !== 'undefined' && typeof window.history !== 'undefined';
  let navigatingViaPopstate = false;

  function pushNav(snapshot: NavHistoryState): void {
    if (!isBrowser || navigatingViaPopstate) return;
    if (!NAVIGABLE_STEPS.has(snapshot.step)) return;
    const current = window.history.state as NavHistoryState | null;
    // Evita empilhar entrada idêntica (ex.: setCurrentStep chamado duas vezes pra dashboard).
    if (
      current !== null &&
      current.step === snapshot.step &&
      current.drillDownId === snapshot.drillDownId &&
      current.focusedEmployeeId === snapshot.focusedEmployeeId
    ) {
      return;
    }
    window.history.pushState(snapshot, '');
  }

  function applyNav(snapshot: NavHistoryState): void {
    state.update((s) => ({
      ...s,
      currentStep: snapshot.step,
      drillDown: snapshot.drillDownId
        ? snapshot.focusedEmployeeId
          ? { metricId: snapshot.drillDownId, focusedEmployeeId: snapshot.focusedEmployeeId }
          : { metricId: snapshot.drillDownId }
        : null,
    }));
  }

  if (isBrowser) {
    window.addEventListener('popstate', (e) => {
      const histState = e.state as NavHistoryState | null;
      navigatingViaPopstate = true;
      try {
        if (histState && NAVIGABLE_STEPS.has(histState.step)) {
          applyNav(histState);
        } else {
          // Saiu da history conhecida (ex.: voltou antes da primeira pushState).
          // Fecha drill-down como mínimo natural; o resto do estado fica.
          state.update((s) => (s.drillDown ? { ...s, drillDown: null } : s));
        }
      } finally {
        navigatingViaPopstate = false;
      }
    });
  }

  function snapshotNav(s: AppState): NavHistoryState {
    return {
      step: s.currentStep,
      drillDownId: s.drillDown?.metricId ?? null,
      focusedEmployeeId: s.drillDown?.focusedEmployeeId ?? null,
    };
  }

  return {
    state,
    acceptPrivacy() {
      state.update((s) => ({ ...s, privacyAccepted: true }));
    },
    reset() {
      state.set({ ...initial, assumptions: { ...DEFAULT_ASSUMPTIONS } });
    },
    clearSession() {
      // Preserva privacyAccepted=true para evitar fricção ao re-analisar (decisao UX T30).
      // currentStep volta para 'import' (nao 'privacy') por decisao de UX.
      state.update((s) => ({
        ...initial,
        assumptions: { ...DEFAULT_ASSUMPTIONS },
        privacyAccepted: s.privacyAccepted,
        currentStep: "import",
        analysisProgress: null,
        result: null,
      }));
      pushNav({ step: 'import', drillDownId: null, focusedEmployeeId: null });
    },
    snapshot() {
      return get(state);
    },
    addLoadedFiles(files: LocalInputFile[]) {
      state.update((s) => ({ ...s, loadedFiles: [...s.loadedFiles, ...files] }));
    },
    updateAssumptions(partial: Partial<DiagnosticAssumptions>) {
      state.update((s) => ({
        ...s,
        assumptions: { ...s.assumptions, ...partial },
      }));
    },
    setCurrentStep(step: AppStep) {
      state.update((s) => ({ ...s, currentStep: step }));
      pushNav({ step, drillDownId: null, focusedEmployeeId: null });
    },
    setAnalysisProgress(progress: WorkerClientState | null) {
      state.update((s) => ({ ...s, analysisProgress: progress }));
    },
    setResult(result: DiagnosticResult | null) {
      state.update((s) => ({ ...s, result }));
    },
    openDrillDown(metricId: string) {
      state.update((s) => ({ ...s, drillDown: { metricId } }));
      pushNav({
        step: get(state).currentStep,
        drillDownId: metricId,
        focusedEmployeeId: null,
      });
    },
    closeDrillDown() {
      // Se a drill-down está na history, prefere navegar via history.back() pra
      // que o popstate aplique a transição (mantém forward funcionando).
      if (
        isBrowser &&
        !navigatingViaPopstate &&
        (window.history.state as NavHistoryState | null)?.drillDownId
      ) {
        window.history.back();
        return;
      }
      state.update((s) => ({ ...s, drillDown: null }));
    },
    focusEmployeeInDrillDown(employeeId: string) {
      state.update((s) => {
        if (!s.drillDown) return s;
        return { ...s, drillDown: { ...s.drillDown, focusedEmployeeId: employeeId } };
      });
      const cur = get(state);
      pushNav(snapshotNav(cur));
    },
    unfocusEmployee() {
      if (
        isBrowser &&
        !navigatingViaPopstate &&
        (window.history.state as NavHistoryState | null)?.focusedEmployeeId
      ) {
        window.history.back();
        return;
      }
      state.update((s) => {
        if (!s.drillDown) return s;
        return { ...s, drillDown: { metricId: s.drillDown.metricId } };
      });
    },
  };
}

/** Singleton conveniente para o app. Testes devem instanciar `createAppStore()` proprio. */
export const appStore: AppStore = createAppStore();
